import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatErr } from "@/lib/api";
import { dapi as api } from "./deskApi";
import { queueIfOffline } from "@/lib/offlineQueue";
import { toast } from "sonner";
import { Folder, FileText, Image as ImageIcon, Upload, FolderPlus, ChevronRight, Home, Download, Pencil, Trash2, PenLine, X, Copy, Scissors, ClipboardPaste, Monitor, Search, FolderOpen, Users, Save, FilePlus, FolderUp, ChevronDown, History } from "lucide-react";
import { restorePrevious } from "./restorePrevious";
import SignatureWorkbench from "@/components/SignatureWorkbench";
import { fsClipboard, clipSet, clipClear, clipHas, DESKTOP_ROOT } from "./clipboard";
import { fileIcon } from "./fileIcon";
import { useLongPressMenu } from "@/lib/useLongPressMenu";

const isImg = (ct) => (ct || "").startsWith("image/");
const TEXT_EXT = /\.(txt|text|md|markdown|csv|tsv|log|json|xml|yml|yaml|ini|conf|cfg|env|js|jsx|ts|css|html|htm|py|sh|sql|bat|cmd|ps1|batch)$/i;
const isText = (item) => (item.content_type || "").startsWith("text/") || TEXT_EXT.test(item.name || "");
const SAVE_AS_EXTS = ["txt", "cmd", "bat", "md", "csv", "json", "html", "log"];
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n || 0} B`);

export default function FileManagerApp({ initialParent = null, initialPreviewId = null, initialSignId = null }) {
  const [parent, setParent] = useState(initialParent);
  const [items, setItems] = useState([]);
  const [crumbs, setCrumbs] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [marquee, setMarquee] = useState(null); // {x0,y0,x1,y1} in client coords
  const marqueeRef = useRef(null);
  const suppressClearRef = useRef(false);
  const [menu, setMenu] = useState(null); // {x, y, item?}
  const [signSelf, setSignSelf] = useState(null); // {item, pages}
  const [links, setLinks] = useState(null); // [{name, email, link}]
  const [signBusy, setSignBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {item, url}
  const [textEdit, setTextEdit] = useState(null); // {item, text, saving}
  const [saveAs, setSaveAs] = useState(null); // {base, ext, custom}
  const [autoSave, setAutoSave] = useState(() => localStorage.getItem("desk_autosave") === "1");
  const [dragOver, setDragOver] = useState(false);
  const [uploadMenu, setUploadMenu] = useState(false);
  const fileRef = useRef(null);
  const folderRef = useRef(null);
  const autoTimer = useRef(null);
  const boxRef = useRef(null);

  const openContextAt = (point, item = null) => {
    const box = boxRef.current?.getBoundingClientRect();
    setMenu({ x: point.clientX - (box?.left || 0), y: point.clientY - (box?.top || 0), item });
  };
  const longPress = useLongPressMenu((point, item) => {
    setSelectedIds(new Set([item.id]));
    openContextAt(point, item);
  });

  const load = useCallback((pid) => {
    api.get("/desktop/fs", { params: pid ? { parent_id: pid } : {} })
      .then((r) => { setItems(r.data.items); setCrumbs(r.data.breadcrumbs); })
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message));
  }, []);

  useEffect(() => { load(parent); }, [parent, load]);
  // refresh when another app (e.g. PDF editor) saves a file
  useEffect(() => {
    const h = () => load(parent);
    window.addEventListener("desk-fs-changed", h);
    return () => window.removeEventListener("desk-fs-changed", h);
  }, [parent, load]);
  useEffect(() => {
    const close = () => { setMenu(null); setUploadMenu(false); };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const previewedRef = useRef(false);
  const signOpenedRef = useRef(false);
  useEffect(() => {
    if (initialPreviewId && !previewedRef.current) {
      const it = items.find((x) => x.id === initialPreviewId);
      if (it) { previewedRef.current = true; openFile(it); }
    }
  }, [items]);
  useEffect(() => {
    if (initialSignId && !signOpenedRef.current) {
      const item = items.find((candidate) => candidate.id === initialSignId && candidate.type === "file");
      if (item) { signOpenedRef.current = true; startSelfSign(item); }
    }
  }, [items, initialSignId]);

  const copyItem = (item, cut = false) => {
    setMenu(null);
    clipSet([item], cut);
    toast.success(`${cut ? "Cut" : "Copied"} "${item.name}" — paste it anywhere.`);
  };

  const copySelected = (cut = false) => {
    setMenu(null);
    const sel = items.filter((i) => selectedIds.has(i.id));
    if (!sel.length) return;
    if (sel.length === 1) return copyItem(sel[0], cut);
    clipSet(sel, cut);
    toast.success(`${cut ? "Cut" : "Copied"} ${sel.length} items — paste them anywhere.`);
  };

  const pasteHere = async (targetParent = parent) => {
    setMenu(null);
    const clip = [...fsClipboard.items];
    if (!clip.length) return toast.error("Nothing to paste — copy or cut something first.");
    const wasCut = fsClipboard.cut;
    let ok = 0;
    for (const c of clip) {
      try {
        if (wasCut) await api.post(`/desktop/fs/${c.id}/move`, { parent_id: targetParent });
        else await api.post(`/desktop/fs/${c.id}/copy`, { parent_id: targetParent });
        ok += 1;
      } catch (e) { toast.error(`${c.name}: ${formatErr(e.response?.data?.detail) || e.message}`); }
    }
    if (wasCut) clipClear();
    if (ok > 1) toast.success(`Pasted ${ok} items.`);
    load(parent);
    window.dispatchEvent(new CustomEvent("desk-fs-changed"));
  };

  const onKeyDown = (e) => {
    const tag = (e.target.tagName || "").toUpperCase();
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    const single = selectedIds.size === 1 ? items.find((x) => x.id === [...selectedIds][0]) : null;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && e.key.toLowerCase() === "a") { e.preventDefault(); setSelectedIds(new Set(items.map((i) => i.id))); }
    else if (ctrl && e.key.toLowerCase() === "c" && selectedIds.size > 0) { e.preventDefault(); copySelected(false); }
    else if (ctrl && e.key.toLowerCase() === "x" && selectedIds.size > 0) { e.preventDefault(); copySelected(true); }
    else if (ctrl && e.key.toLowerCase() === "v") { e.preventDefault(); pasteHere(); }
    else if (e.key === "Delete" && selectedIds.size > 0) { e.preventDefault(); removeMany(); }
    else if (e.key === "F2" && single) { e.preventDefault(); rename(single); }
    else if (e.key === "Enter" && single) { e.preventDefault(); single.type === "folder" ? setParent(single.id) : openFile(single); }
  };

  const clickItem = (e, it) => {
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) {
      setSelectedIds((prev) => { const n = new Set(prev); n.has(it.id) ? n.delete(it.id) : n.add(it.id); return n; });
    } else {
      setSelectedIds(new Set([it.id]));
    }
  };

  const startMarquee = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('[data-fm-id]') || e.target.closest('[data-testid="fm-desktop-link"]')) return;
    const additive = e.ctrlKey || e.metaKey;
    const base = additive ? new Set(selectedIds) : new Set();
    marqueeRef.current = { x0: e.clientX, y0: e.clientY, moved: false };
    setMarquee({ x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY });
    if (!additive) setSelectedIds(new Set());
    const move = (ev) => {
      const m = marqueeRef.current; if (!m) return;
      if (Math.abs(ev.clientX - m.x0) + Math.abs(ev.clientY - m.y0) > 4) m.moved = true;
      setMarquee({ x0: m.x0, y0: m.y0, x1: ev.clientX, y1: ev.clientY });
      const rx0 = Math.min(m.x0, ev.clientX), rx1 = Math.max(m.x0, ev.clientX);
      const ry0 = Math.min(m.y0, ev.clientY), ry1 = Math.max(m.y0, ev.clientY);
      const next = new Set(base);
      boxRef.current?.querySelectorAll("[data-fm-id]").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.left < rx1 && r.right > rx0 && r.top < ry1 && r.bottom > ry0) next.add(el.getAttribute("data-fm-id"));
      });
      setSelectedIds(next);
    };
    const up = () => {
      if (marqueeRef.current?.moved) { suppressClearRef.current = true; setTimeout(() => { suppressClearRef.current = false; }, 0); }
      marqueeRef.current = null;
      setMarquee(null);
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const clearSelection = () => { if (suppressClearRef.current) return; setSelectedIds(new Set()); };

  const newFolder = async () => {
    setMenu(null);
    const name = window.prompt("Folder name:", "New folder");
    if (name === null) return;
    try {
      await api.post("/desktop/fs/folder", { name, parent_id: parent });
      load(parent);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const upload = async (files) => {
    for (const f of Array.from(files || [])) {
      const fd = new FormData();
      fd.append("file", f);
      if (parent) fd.append("parent_id", parent);
      try {
        await api.post("/desktop/fs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) { toast.error(`${f.name}: ${formatErr(e.response?.data?.detail) || e.message}`); }
    }
    load(parent);
  };

  const uploadStructured = async (list) => {
    // list: [{file, dir}] — dir is the relative folder path ("" = current folder)
    if (!list.length) return;
    const folderIds = {};
    const ensureFolder = async (path) => {
      if (folderIds[path]) return folderIds[path];
      const parts = path.split("/");
      const parentPath = parts.slice(0, -1).join("/");
      const pid = parentPath ? await ensureFolder(parentPath) : parent;
      const { data } = await api.post("/desktop/fs/folder", { name: parts[parts.length - 1], parent_id: pid });
      folderIds[path] = data.id;
      return data.id;
    };
    if (list.length > 1) toast.info(`Uploading ${list.length} files…`);
    let ok = 0;
    for (const { file: f, dir } of list) {
      try {
        const pid = dir ? await ensureFolder(dir) : parent;
        const fd = new FormData();
        fd.append("file", f);
        if (pid) fd.append("parent_id", pid);
        await api.post("/desktop/fs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) { toast.error(`${f.name}: ${formatErr(e.response?.data?.detail) || e.message}`); }
    }
    toast.success(ok === 1 ? "Uploaded 1 file." : `Uploaded ${ok} files.`);
    load(parent);
    window.dispatchEvent(new CustomEvent("desk-fs-changed"));
  };

  const uploadFolder = (files) =>
    uploadStructured(Array.from(files || []).map((f) => {
      const rel = f.webkitRelativePath || f.name;
      return { file: f, dir: rel.includes("/") ? rel.split("/").slice(0, -1).join("/") : "" };
    }));

  // Drag & drop: works automatically for BOTH files and whole folders.
  const collectEntry = async (entry, base, out) => {
    if (entry.isFile) {
      const f = await new Promise((res, rej) => entry.file(res, rej));
      out.push({ file: f, dir: base });
    } else if (entry.isDirectory) {
      const dirPath = base ? `${base}/${entry.name}` : entry.name;
      const reader = entry.createReader();
      let batch;
      do {
        batch = await new Promise((res, rej) => reader.readEntries(res, rej));
        for (const e of batch) await collectEntry(e, dirPath, out);
      } while (batch.length);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const entries = Array.from(e.dataTransfer.items || []).map((i) => i.webkitGetAsEntry?.()).filter(Boolean);
    if (!entries.length) return upload(e.dataTransfer.files);
    const out = [];
    try {
      for (const en of entries) await collectEntry(en, "", out);
    } catch { return upload(e.dataTransfer.files); }
    uploadStructured(out);
  };

  const rename = async (item) => {
    setMenu(null);
    const name = window.prompt("Rename to:", item.name);
    if (name === null || !name.trim()) return;
    try { await api.patch(`/desktop/fs/${item.id}`, { name }); load(parent); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const remove = async (item) => {
    setMenu(null);
    if (!window.confirm(`Move "${item.name}"${item.type === "folder" ? " and everything inside it" : ""} to the Recycle Bin?`)) return;
    try {
      await api.delete(`/desktop/fs/${item.id}`);
      toast.success(`"${item.name}" moved to the Recycle Bin.`);
      load(parent);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const removeMany = async () => {
    setMenu(null);
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (ids.length === 1) { const it = items.find((i) => i.id === ids[0]); if (it) return remove(it); }
    if (!window.confirm(`Move ${ids.length} items to the Recycle Bin?`)) return;
    try {
      await Promise.all(ids.map((id) => api.delete(`/desktop/fs/${id}`)));
      toast.success(`${ids.length} items moved to the Recycle Bin.`);
      setSelectedIds(new Set());
      load(parent);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const download = async (item) => {
    setMenu(null);
    try {
      const r = await api.get(`/desktop/fs/${item.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = item.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const openFile = async (item) => {
    if (item.content_type === "application/pdf") {
      // PDFs open in PdfCraft, the default PDF reader
      window.dispatchEvent(new CustomEvent("desk-open-app", {
        detail: { app: "pdfcraft", props: { file: { id: item.id, name: item.name } } },
      }));
      return;
    }
    if (/\.(cdoc|docx)$/i.test(item.name)) {
      window.dispatchEvent(new CustomEvent("desk-open-app", {
        detail: { app: "word", props: { file: { id: item.id, name: item.name } } },
      }));
      return;
    }
    if (/\.(csheet|xlsx|xls)$/i.test(item.name)) {
      window.dispatchEvent(new CustomEvent("desk-open-app", {
        detail: { app: "sheets", props: { file: { id: item.id, name: item.name } } },
      }));
      return;
    }
    if (isText(item)) {
      try {
        const { data } = await api.get(`/desktop/fs/${item.id}/text`);
        setTextEdit({ item, text: data.content || "", saving: false });
      } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
      return;
    }
    try {
      const r = await api.get(`/desktop/fs/${item.id}/download`, { responseType: "blob" });
      const fr = new FileReader();
      fr.onload = () => setPreview({ item, url: fr.result });
      fr.readAsDataURL(r.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const saveText = async () => {
    if (!textEdit) return;
    clearTimeout(autoTimer.current);
    setTextEdit((t) => ({ ...t, saving: true }));
    try {
      await api.put(`/desktop/fs/${textEdit.item.id}/text`, { content: textEdit.text });
      toast.success(`Saved "${textEdit.item.name}"`);
      load(parent);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
      setTextEdit(null);
      setSaveAs(null);
    } catch (e) {
      if (queueIfOffline(e, { portal: "desk", method: "put", path: `/desktop/fs/${textEdit.item.id}/text`, body: { content: textEdit.text }, label: `Save "${textEdit.item.name}"` })) {
        setTextEdit(null);
        setSaveAs(null);
        return;
      }
      toast.error(formatErr(e.response?.data?.detail) || e.message);
      setTextEdit((t) => ({ ...t, saving: false }));
    }
  };

  const quietSave = async (item, text) => {
    try {
      await api.put(`/desktop/fs/${item.id}/text`, { content: text });
      setTextEdit((t) => (t && t.item.id === item.id ? { ...t, dirty: false, savedAt: Date.now() } : t));
    } catch { /* retried on next keystroke */ }
  };

  const closeTextEdit = () => {
    clearTimeout(autoTimer.current);
    setTextEdit(null);
    setSaveAs(null);
    load(parent);
    window.dispatchEvent(new CustomEvent("desk-fs-changed"));
  };

  const saveAsExt = async (ext) => {
    setSaveAs(null);
    const clean = (ext || "").replace(/^\.+/, "").replace(/[^a-zA-Z0-9]/g, "").trim().toLowerCase();
    if (!clean || !textEdit) return;
    const base = textEdit.item.name.includes(".") ? textEdit.item.name.replace(/\.[^.]+$/, "") : textEdit.item.name;
    const newName = `${base}.${clean}`;
    try {
      await api.patch(`/desktop/fs/${textEdit.item.id}`, { name: newName });
      await api.put(`/desktop/fs/${textEdit.item.id}/text`, { content: textEdit.text });
      setTextEdit((t) => ({ ...t, item: { ...t.item, name: newName }, dirty: false, savedAt: Date.now() }));
      toast.success(`Saved as "${newName}"`);
      load(parent);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const newTextFile = async () => {
    const name = window.prompt("Name your text file", "Notes.txt");
    if (name === null) return;
    try {
      const { data } = await api.post("/desktop/fs/text", { name: name.trim() || "Notes.txt", parent_id: parent || null, content: "" });
      load(parent);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
      setTextEdit({ item: data, text: "", saving: false });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const startSelfSign = async (item) => {
    setPreview(null);
    try {
      const r = await api.get(`/desktop/fs/${item.id}/pages`);
      setSignSelf({ item, pages: r.data.pages || [] });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const sendRequest = async (item, signers, message, fields = [], signingOrder = "parallel", autoRemind = false, remindDays = 3, edits = []) => {
    setSignBusy(true);
    try {
      const { data } = await api.post("/desktop/signatures", {
        file_id: item.id,
        message,
        signing_order: signingOrder,
        auto_remind: autoRemind,
        remind_days: remindDays,
        edits,
        signers: signers.map((s, i) => ({
          name: s.name.trim(), email: s.email.trim() || null,
          fields: fields.filter((f) => (f.signer ?? 0) === i).map(({ signer, ...f }) => f),
        })),
      });
      setSignSelf(null);
      setLinks(data.signers);
      if (data.email_errors?.length) {
        toast.warning(`Request saved, but some emails failed: ${data.email_errors.join("; ")}. Copy their links below or use Remind from the Signatures app.`);
      } else {
        const emailed = data.signers.filter((s) => s.email).length;
        toast.success(emailed ? `Signing links emailed to ${emailed} signer${emailed > 1 ? "s" : ""}.` : "Signing links created.");
      }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSignBusy(false); }
  };

  const onCtx = (e, item = null) => {
    e.preventDefault();
    e.stopPropagation();
    openContextAt(e, item);
  };

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/desktop/fs-search", { params: { q } });
        setResults(data.items || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const goToLocation = (n) => {
    setQuery("");
    setParent(n.parent_id === DESKTOP_ROOT ? DESKTOP_ROOT : n.parent_id || null);
  };

  return (
    <div ref={boxRef} tabIndex={0} onKeyDown={onKeyDown} className="relative h-full flex flex-col text-slate-800 outline-none" onContextMenu={(e) => onCtx(e)} onClick={clearSelection}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (!boxRef.current?.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={onDrop}>
      <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-200 text-sm bg-slate-50">
        <button type="button" className="p-1 rounded hover:bg-slate-200" onClick={() => setParent(null)} data-testid="fm-home">
          <Home className="w-4 h-4" />
        </button>
        {crumbs.map((c) => (
          <React.Fragment key={c.id}>
            <ChevronRight className="w-3 h-3 text-slate-400" />
            <button type="button" className="px-1 rounded hover:bg-slate-200 font-medium" onClick={() => setParent(c.id)}>{c.name}</button>
          </React.Fragment>
        ))}
        <span className="flex-1" />
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search files & folders…"
            className="w-44 sm:w-56 text-xs border border-slate-300 rounded-sm pl-7 pr-6 py-1.5 bg-white outline-none focus:border-sky-500"
            data-testid="fm-search-input"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="fm-search-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button type="button" className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded hover:bg-slate-200" onClick={newFolder} data-testid="fm-new-folder">
          <FolderPlus className="w-4 h-4" /> New folder
        </button>
        <div className="relative">
          <button type="button" className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded hover:bg-slate-200" onClick={(e) => { e.stopPropagation(); setUploadMenu((v) => !v); }} data-testid="fm-upload">
            <Upload className="w-4 h-4" /> Upload <ChevronDown className="w-3 h-3" />
          </button>
          {uploadMenu && (
            <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-xl py-1 w-60 text-sm" onClick={(e) => e.stopPropagation()} data-testid="fm-upload-menu">
              <MenuItem icon={FileText} label="Files…" onClick={() => { setUploadMenu(false); fileRef.current?.click(); }} testid="fm-upload-files" />
              <MenuItem icon={FolderUp} label="Whole folder (with everything inside)…" onClick={() => { setUploadMenu(false); folderRef.current?.click(); }} testid="fm-upload-folder-opt" />
              <p className="px-3 pt-1.5 pb-0.5 text-[10px] text-slate-400 border-t border-slate-100 mt-1">Tip: drag files or folders straight into this window — it uploads automatically.</p>
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="fm-upload-input" />
        <input ref={folderRef} type="file" webkitdirectory="" multiple className="hidden" onChange={(e) => { uploadFolder(e.target.files); e.target.value = ""; }} data-testid="fm-upload-folder-input" />
      </div>

      <div className="flex-1 overflow-auto p-3" onMouseDown={results === null ? startMarquee : undefined}>
        {results !== null ? (
          <div data-testid="fm-search-results">
            <p className="text-xs text-slate-400 mb-2">
              {results.length} result{results.length === 1 ? "" : "s"} for &quot;{query.trim()}&quot;
            </p>
            {results.length === 0 ? (
              <p className="text-sm text-slate-400 text-center mt-10">Nothing found.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {results.map((n) => {
                  const { Icon, color } = fileIcon(n);
                  return (
                    <li key={n.id} className="flex items-center gap-2.5 px-2 py-2 hover:bg-sky-50 rounded text-sm" data-testid={`fm-search-item-${n.id}`}>
                      <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (n.type === "folder") { setQuery(""); setParent(n.id); }
                          else openFile(n);
                        }}
                        className="truncate text-left hover:underline font-medium"
                        title={n.type === "folder" ? "Open folder" : "Open file"}
                      >
                        {n.name}
                      </button>
                      <span className="text-[11px] text-slate-400 truncate flex-1">{n.path}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); goToLocation(n); }}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:underline shrink-0"
                        title="Open the folder that contains this item"
                        data-testid={`fm-search-locate-${n.id}`}
                      >
                        <FolderOpen className="w-3.5 h-3.5" /> Open location
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : (
        <>
        {items.length === 0 && parent !== null && (
          <p className="text-sm text-slate-400 text-center mt-10">Empty — right-click to create a folder, or upload files.</p>
        )}
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {parent === null && (
            <button
              type="button"
              onDoubleClick={() => setParent(DESKTOP_ROOT)}
              onClick={(e) => e.stopPropagation()}
              className="flex flex-col items-center gap-1 p-2 rounded border border-transparent hover:bg-sky-50 hover:border-sky-200"
              title="Desktop — files placed on the desktop live here (double-click to open)"
              data-testid="fm-desktop-link"
            >
              <Monitor className="w-10 h-10 text-sky-500" strokeWidth={1.2} />
              <span className="text-[11px] leading-tight text-center">Desktop</span>
            </button>
          )}
          {items.map((it) => {
            const { Icon, color } = fileIcon(it);
            return (
            <button
              key={it.id}
              type="button"
              data-fm-id={it.id}
              onClick={(e) => { if (!longPress.consumeClick(e)) clickItem(e, it); }}
              onDoubleClick={() => (it.type === "folder" ? setParent(it.id) : openFile(it))}
              onContextMenu={(e) => { if (!selectedIds.has(it.id)) setSelectedIds(new Set([it.id])); onCtx(e, it); }}
              onPointerDown={(e) => longPress.start(e, it)}
              className={`flex flex-col items-center gap-1 p-2 rounded border ${selectedIds.has(it.id) ? "bg-sky-100 border-sky-300" : "border-transparent hover:bg-sky-50 hover:border-sky-200"} ${fsClipboard.cut && clipHas(it.id) ? "opacity-50" : ""}`}
              title={`${it.name}${it.updated_by ? ` — last edited by ${it.updated_by}` : ""} — double-click to open`}
              data-testid={`fm-item-${it.id}`}
              style={{ WebkitTouchCallout: "none", touchAction: "manipulation" }}
            >
              {it.type === "folder"
                ? <span className="relative">
                    <Folder className={`w-10 h-10 ${it.shared ? "text-sky-500 fill-sky-300" : "text-amber-400 fill-amber-300"}`} strokeWidth={1} />
                    {it.shared && <Users className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-white bg-sky-600 rounded-full p-0.5" strokeWidth={2.2} />}
                  </span>
                : <span className="relative">
                    <Icon className={`w-10 h-10 ${color}`} strokeWidth={1.2} />
                    {it.shared && <Users className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-white bg-sky-600 rounded-full p-0.5" strokeWidth={2.2} />}
                  </span>}
              <span className="text-[11px] leading-tight text-center break-all line-clamp-2">{it.name}</span>
              {it.type === "file" && (
                <span className="text-[10px] text-slate-400 text-center leading-tight">
                  {fmtSize(it.size)}
                  {it.shared && it.updated_by && <span className="block text-sky-600 truncate max-w-[90px]" data-testid={`fm-edited-by-${it.id}`}>✎ {it.updated_by}</span>}
                </span>
              )}
            </button>
            );
          })}
        </div>
        </>
        )}
      </div>

      {dragOver && (
        <div className="absolute inset-0 z-[70] bg-sky-500/10 border-2 border-dashed border-sky-500 rounded-md grid place-items-center pointer-events-none" data-testid="fm-drop-overlay">
          <p className="bg-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold text-sky-700">Drop files or folders here — folders upload with everything inside</p>
        </div>
      )}

      {marquee && (() => {
        const box = boxRef.current?.getBoundingClientRect();
        if (!box) return null;
        const left = Math.min(marquee.x0, marquee.x1) - box.left;
        const top = Math.min(marquee.y0, marquee.y1) - box.top;
        const width = Math.abs(marquee.x1 - marquee.x0);
        const height = Math.abs(marquee.y1 - marquee.y0);
        return (
          <div
            className="absolute z-40 border border-sky-400 bg-sky-400/15 pointer-events-none rounded-sm"
            style={{ left, top, width, height }}
            data-testid="fm-marquee"
          />
        );
      })()}

      {selectedIds.size > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-full shadow-lg flex items-center gap-3" data-testid="fm-selection-bar">
          {selectedIds.size} selected
          <button type="button" className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 font-semibold" onClick={() => copySelected(false)} data-testid="fm-selection-copy">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button type="button" className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 font-semibold" onClick={() => copySelected(true)} data-testid="fm-selection-cut">
            <Scissors className="w-3.5 h-3.5" /> Cut
          </button>
          <button type="button" className="inline-flex items-center gap-1 text-red-300 hover:text-red-200 font-semibold" onClick={removeMany} data-testid="fm-selection-delete">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}

      {menu && (
        <div
          className="absolute z-50 bg-white border border-slate-200 rounded-md shadow-xl py-1 w-48 text-sm"
          style={{ left: Math.min(menu.x, (boxRef.current?.clientWidth || 400) - 200), top: menu.y }}
          onClick={(e) => e.stopPropagation()}
          data-testid="fm-context-menu"
        >
          {!menu.item && (
            <>
              <MenuItem icon={FolderPlus} label="New folder" onClick={newFolder} testid="fm-ctx-new-folder" />
              <MenuItem icon={FilePlus} label="New text file" onClick={() => { setMenu(null); newTextFile(); }} testid="fm-ctx-new-text" />
              <MenuItem icon={Upload} label="Upload files" onClick={() => { setMenu(null); fileRef.current?.click(); }} />
              <MenuItem icon={FolderUp} label="Upload folder" onClick={() => { setMenu(null); folderRef.current?.click(); }} testid="fm-ctx-upload-folder" />
              {fsClipboard.items.length > 0 && (
                <MenuItem icon={ClipboardPaste} label={fsClipboard.items.length === 1 ? `Paste "${fsClipboard.items[0].name}"` : `Paste ${fsClipboard.items.length} items`} onClick={() => pasteHere()} testid="fm-ctx-paste" />
              )}
            </>
          )}
          {menu.item && menu.item.type === "folder" && !(selectedIds.size > 1 && selectedIds.has(menu.item.id)) && (
            <>
              <MenuItem icon={Folder} label="Open" onClick={() => { setMenu(null); setParent(menu.item.id); }} />
              {fsClipboard.items.length > 0 && !clipHas(menu.item.id) && (
                <MenuItem icon={ClipboardPaste} label="Paste into folder" onClick={() => pasteHere(menu.item.id)} testid="fm-ctx-paste-into" />
              )}
            </>
          )}
          {menu.item && menu.item.type === "file" && !(selectedIds.size > 1 && selectedIds.has(menu.item.id)) && (
            <>
              <MenuItem icon={FileText} label="Open" onClick={() => { setMenu(null); openFile(menu.item); }} testid="fm-ctx-open" />
              <MenuItem icon={Download} label="Download" onClick={() => download(menu.item)} testid="fm-ctx-download" />
              {menu.item.has_previous && <MenuItem icon={History} label="Restore previous version" onClick={() => { const it = menu.item; setMenu(null); restorePrevious(it, () => load(parent)); }} testid="fm-ctx-restore-previous" />}
              <MenuItem icon={PenLine} label="Fill out & sign / Send to sign" onClick={() => { const it = menu.item; setMenu(null); startSelfSign(it); }} testid="fm-ctx-self-sign" />
            </>
          )}
          {menu.item && menu.item.id !== "__public__" && (
            <>
              {selectedIds.size > 1 && selectedIds.has(menu.item.id) ? (
                <>
                  <MenuItem icon={Copy} label={`Copy ${selectedIds.size} items`} onClick={() => copySelected(false)} testid="fm-ctx-copy-many" />
                  <MenuItem icon={Scissors} label={`Cut ${selectedIds.size} items`} onClick={() => copySelected(true)} testid="fm-ctx-cut-many" />
                  <MenuItem icon={Trash2} label={`Delete ${selectedIds.size} items`} danger onClick={removeMany} testid="fm-ctx-delete-many" />
                </>
              ) : (
                <>
                  <MenuItem icon={Copy} label="Copy" onClick={() => copyItem(menu.item, false)} testid="fm-ctx-copy" />
                  <MenuItem icon={Scissors} label="Cut" onClick={() => copyItem(menu.item, true)} testid="fm-ctx-cut" />
                  <MenuItem icon={Pencil} label="Rename" onClick={() => rename(menu.item)} />
                  <MenuItem icon={Trash2} label="Delete" danger onClick={() => remove(menu.item)} />
                </>
              )}
            </>
          )}
        </div>
      )}

      {links && (
        <div className="absolute inset-0 z-[60] bg-black/30 flex items-center justify-center p-4" onClick={() => setLinks(null)}>
          <div className="bg-white rounded-lg shadow-2xl p-4 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()} data-testid="fm-links-dialog">
            <h4 className="font-bold text-sm">✓ Signature request sent</h4>
            {links.map((s, i) => (
              <div key={i} className="text-xs space-y-1">
                <p className="font-semibold text-slate-700">{s.name || `Signer ${i + 1}`}{s.email ? ` · ${s.email} (emailed)` : ""}</p>
                <div className="flex gap-1.5">
                  <input readOnly className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-xs bg-slate-50" value={s.link} onFocus={(e) => e.target.select()} data-testid={`fm-sign-link-${i}`} />
                  <button
                    type="button"
                    className="px-2.5 rounded bg-slate-800 text-white"
                    onClick={async () => { try { await navigator.clipboard.writeText(s.link); toast.success("Link copied."); } catch { window.prompt("Copy the signing link:", s.link); } }}
                    title="Copy link"
                    data-testid={`fm-sign-copy-${i}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <button type="button" className="text-sm px-3 py-1.5 rounded border border-slate-300" onClick={() => setLinks(null)} data-testid="fm-links-done">Done</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="absolute inset-0 z-[55] bg-black/40 flex items-center justify-center p-3" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-full overflow-y-auto p-4" onClick={(e) => e.stopPropagation()} data-testid="fm-preview">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-sm truncate pr-2">{preview.item.name}</h4>
              <button type="button" className="p-1 rounded hover:bg-slate-100" onClick={() => setPreview(null)} data-testid="fm-preview-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="border border-slate-200 rounded overflow-hidden bg-slate-50 mb-3">
              {isImg(preview.item.content_type) && <img src={preview.url} alt={preview.item.name} className="w-full" />}
              {preview.item.content_type === "application/pdf" && <iframe src={preview.url} title={preview.item.name} className="w-full h-[380px]" />}
              {!isImg(preview.item.content_type) && preview.item.content_type !== "application/pdf" && (
                <p className="p-6 text-center text-sm text-slate-500">No preview for this file type — download it below.</p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap mt-3">
              <ActionBtn onClick={() => download(preview.item)} testid="fm-preview-download"><Download className="w-3.5 h-3.5" /> Download</ActionBtn>
              <ActionBtn onClick={() => startSelfSign(preview.item)} testid="fm-preview-self-sign"><PenLine className="w-3.5 h-3.5" /> Fill out &amp; sign / Send to sign</ActionBtn>
            </div>
          </div>
        </div>
      )}

      {textEdit && (
        <div className="absolute inset-0 z-[56] bg-black/40 flex items-center justify-center p-3" onClick={closeTextEdit}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl h-[80%] flex flex-col" onClick={(e) => { e.stopPropagation(); setSaveAs(null); }} data-testid="fm-text-editor">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
              <h4 className="font-bold text-sm truncate pr-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-sky-600" /> {textEdit.item.name}
                {textEdit.item.updated_by && (
                  <span className="text-[11px] font-normal text-slate-400" data-testid="fm-text-last-edited">Last edited by {textEdit.item.updated_by}</span>
                )}
              </h4>
              <button type="button" className="p-1 rounded hover:bg-slate-100" onClick={closeTextEdit} data-testid="fm-text-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={textEdit.text}
              onChange={(e) => {
                const v = e.target.value;
                setTextEdit((t) => ({ ...t, text: v, dirty: true }));
                if (autoSave) {
                  clearTimeout(autoTimer.current);
                  autoTimer.current = setTimeout(() => quietSave(textEdit.item, v), 1200);
                }
              }}
              onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); saveText(); } }}
              className="flex-1 w-full resize-none p-4 text-sm outline-none font-mono leading-relaxed"
              placeholder="Empty file — start typing…"
              spellCheck={false}
              data-testid="fm-text-area"
            />
            <div className="flex gap-2 items-center px-4 py-2.5 border-t border-slate-200">
              <ActionBtn onClick={saveText} testid="fm-text-save">
                <Save className="w-3.5 h-3.5" /> {textEdit.saving ? "Saving…" : "Save"}
              </ActionBtn>
              <div className="relative">
                <ActionBtn onClick={(e) => { e?.stopPropagation?.(); setSaveAs((s) => (s ? null : { open: true })); }} testid="fm-text-saveas">
                  Save as .{(textEdit.item.name.match(/\.([^.]+)$/) || [null, "txt"])[1]} <ChevronDown className="w-3.5 h-3.5" />
                </ActionBtn>
                {saveAs && (
                  <div className="absolute bottom-full mb-1 left-0 bg-white border border-slate-200 rounded-md shadow-xl py-1 w-44 z-10 max-h-56 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="fm-saveas-menu">
                    <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Save with extension</p>
                    {SAVE_AS_EXTS.map((x) => (
                      <button key={x} type="button" onClick={() => saveAsExt(x)}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 font-mono" data-testid={`fm-saveas-${x}`}>.{x}</button>
                    ))}
                    <button type="button" onClick={() => { const v = window.prompt("Type the extension (without the dot):", ""); if (v !== null) saveAsExt(v); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 text-sky-700 font-semibold border-t border-slate-100" data-testid="fm-saveas-custom">Type your own…</button>
                  </div>
                )}
              </div>
              <ActionBtn onClick={() => download(textEdit.item)} testid="fm-text-download"><Download className="w-3.5 h-3.5" /> Download</ActionBtn>
              <span className="ml-auto flex items-center gap-3">
                {textEdit.savedAt && !textEdit.dirty && <span className="text-[11px] text-emerald-600 font-semibold" data-testid="fm-text-saved-badge">Saved ✓</span>}
                <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none" title="Save automatically a moment after you stop typing">
                  <input type="checkbox" checked={autoSave}
                    onChange={(e) => { setAutoSave(e.target.checked); localStorage.setItem("desk_autosave", e.target.checked ? "1" : "0"); }}
                    data-testid="fm-text-autosave" />
                  Auto-save
                </label>
                <span className="text-[11px] text-slate-400">Ctrl/⌘+S to save</span>
              </span>
            </div>
          </div>
        </div>
      )}


      {signSelf && (
        <SignatureWorkbench
          pages={signSelf.pages}
          docUrl={null}
          title={signSelf.item.name}
          submitLabel="SIGN & SAVE"
          askName={false}
          allowSend
          desktopMode
          busy={signBusy}
          onClose={() => setSignSelf(null)}
          onSend={({ signers, message, fields, signing_order, auto_remind, remind_days, edits }) =>
            sendRequest(signSelf.item, signers, message, fields, signing_order, auto_remind, remind_days, edits)}
          onSubmit={async (payload) => {
            setSignBusy(true);
            try {
              await api.post(`/desktop/fs/${signSelf.item.id}/self-sign`, payload);
              toast.success('Signed copy saved in "Signed documents".');
              setSignSelf(null);
              load(parent);
            } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
            finally { setSignBusy(false); }
          }}
        />
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100 text-left ${danger ? "text-red-600" : ""}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function ActionBtn({ children, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded border border-slate-300 hover:bg-slate-100">
      {children}
    </button>
  );
}


